import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Group, Stack, Text, TextInput } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { UserSelector } from '@/components/Users/UserSelector';
import { Icon } from '@/ui';
import { ChecklistPreviewTask } from './templateChecklist';

export function TemplateChecklistPreview({
  tasks,
  onChange,
}: {
  tasks: ChecklistPreviewTask[];
  onChange: (tasks: ChecklistPreviewTask[]) => void;
}) {
  const { t } = useTranslation();

  if (!tasks.length) {
    return null;
  }

  const updateTask = (index: number, patch: Partial<ChecklistPreviewTask>) => {
    onChange(tasks.map((task, idx) => (idx === index ? { ...task, ...patch } : task)));
  };

  const removeTask = (index: number) => {
    onChange(tasks.filter((_, idx) => idx !== index));
  };

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        {t('posts.checklist')}
      </Text>
      <Stack gap="xs">
        {tasks.map((task, idx) => (
          <Group key={task.id} gap="xs" wrap="nowrap" align="flex-start">
            <TextInput
              size="xs"
              flex={1}
              placeholder={t('posts.title')}
              value={task.title}
              onChange={(event) => updateTask(idx, { title: event.target.value })}
            />
            <DateTimePicker
              size="xs"
              w={190}
              placeholder={t('posts.due-date')}
              clearable
              value={task.dueDate ? dayjs(task.dueDate).format() : null}
              onChange={(value) =>
                updateTask(idx, {
                  dueDate: value ? dayjs(value).toDate() : null,
                  manualDueDate: true,
                })
              }
            />
            <UserSelector
              size="xs"
              w={170}
              placeholder={t('posts.select-owner')}
              clearable
              value={task.userId}
              onChange={(value) => updateTask(idx, { userId: value ?? null })}
            />
            <ActionIcon
              size="md"
              color="red"
              variant="subtle"
              aria-label={t('posts.delete')}
              onClick={() => removeTask(idx)}
            >
              <Icon name="trash" />
            </ActionIcon>
          </Group>
        ))}
      </Stack>
    </Stack>
  );
}
